import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import BreadcrumbNavigation from '../../components/BreadcrumbNavigation';
import QuickActionToolbar from '../../components/QuickActionToolbar';
import ExpenseCategoryTree from './components/ExpenseCategoryTree';
import QuickEntryForm from './components/QuickEntryForm';
import ExpenseGrid from './components/ExpenseGrid';
import FilterToolbar from './components/FilterToolbar';
import ApprovalWorkflow from './components/ApprovalWorkflow';

import Button from '../../components/ui/Button';

import * as exportHelpers from '../../utils/exportHelpers';

import ExportModal from './components/ExportModal';

// Import the properly configured Supabase client
import { supabase } from '../../lib/supabase';

// Define expense categories
const EXPENSE_CATEGORIES = [
  { id: 'salaries', name: 'Salários e Encargos' },
  { id: 'rent', name: 'Aluguel' },
  { id: 'utilities', name: 'Utilidades (Luz, Água, Internet)' },
  { id: 'fuel', name: 'Combustível' },
  { id: 'maintenance', name: 'Manutenção de Veículos' },
  { id: 'insurance', name: 'Seguros' },
  { id: 'office', name: 'Material de Escritório' },
  { id: 'marketing', name: 'Marketing e Publicidade' },
  { id: 'professional', name: 'Serviços Profissionais' },
  { id: 'taxes', name: 'Impostos e Taxas' },
  { id: 'other', name: 'Outras Despesas' }
];

import { useRealtimeSubscriptions } from '../../hooks/useRealtimeSubscriptions';
import ConnectionStatusIndicator from '../../components/ConnectionStatusIndicator';

export default function ExpenseManagementSystem() {
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showApprovalWorkflow, setShowApprovalWorkflow] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showQuickEntry, setShowQuickEntry] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Setup real-time updates with connection monitoring
  const realtimeStatus = useRealtimeSubscriptions({
    onExpenseChange: () => loadExpenses(),
    enabled: true
  });

  const ITEMS_PER_PAGE = 25;

  // Optimize expense query to use pagination
  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch with pagination for better performance
      const { data, error } = await supabase
        ?.from('expense')
        ?.select('*')
        ?.order('expense_date', { ascending: false })
        ?.range(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE - 1);
        
      if (error) throw error;
      
      setExpenses(data || []);
      
      // Get total count for pagination
      const { count } = await supabase
        ?.from('expense')
        ?.select('*', { count: 'exact', head: true });
        
      setTotalPages(Math.ceil((count || 0) / ITEMS_PER_PAGE));
      
    } catch (err) {
      console.error('Error loading expenses:', err);
      setError(err?.message || 'Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Load expenses from localStorage
      const storedExpenses = localStorage.getItem('jrwise_expenses');
      if (storedExpenses) {
        const expensesData = JSON.parse(storedExpenses);
        setExpenses(expensesData);
        setFilteredExpenses(expensesData);
      }
    } catch (err) {
      console.error('Error loading expenses:', err);
      setError('Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  // Save expenses to localStorage whenever they change
  useEffect(() => {
    if (expenses?.length > 0) {
      localStorage.setItem('jrwise_expenses', JSON.stringify(expenses));
    }
  }, [expenses]);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleFilterChange = (filters) => {
    let filtered = [...expenses];

    if (filters?.search) {
      filtered = filtered?.filter((exp) =>
        exp?.vendor?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        exp?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    if (filters?.category) {
      filtered = filtered?.filter((exp) => exp?.category === filters?.category);
    }

    if (filters?.status) {
      filtered = filtered?.filter((exp) => exp?.status === filters?.status);
    }

    if (filters?.client) {
      filtered = filtered?.filter((exp) => exp?.client && exp?.client?.includes(filters?.client));
    }

    if (filters?.dateFrom) {
      filtered = filtered?.filter((exp) => new Date(exp.date) >= new Date(filters.dateFrom));
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter((exp) => new Date(exp.date) <= new Date(filters.dateTo));
    }

    if (filters?.minAmount) {
      filtered = filtered?.filter((exp) => exp?.amount >= parseFloat(filters?.minAmount));
    }

    if (filters?.maxAmount) {
      filtered = filtered?.filter((exp) => exp?.amount <= parseFloat(filters?.maxAmount));
    }

    setFilteredExpenses(filtered);
  };

  const handleExpenseSubmit = (formData) => {
    if (editingExpense) {
      // Update existing expense
      const updatedExpense = {
        ...editingExpense,
        date: formData?.date,
        vendor: formData?.vendor,
        category: formData?.category,
        amount: parseFloat(formData?.amount),
        client: formData?.client || null,
        description: formData?.description,
        receipt: formData?.receipt ? URL.createObjectURL(formData?.receipt) : editingExpense?.receipt,
        receiptAlt: formData?.receipt ? `Receipt document for ${formData?.vendor} expense showing payment details and transaction information` : editingExpense?.receiptAlt,
        bank: formData?.bank || null
      };

      setExpenses(expenses?.map(exp => exp?.id === editingExpense?.id ? updatedExpense : exp));
      setFilteredExpenses(filteredExpenses?.map(exp => exp?.id === editingExpense?.id ? updatedExpense : exp));
      setEditingExpense(null);
    } else {
      // Create new expense
      const newExpense = {
        id: Date.now(),
        date: formData?.date,
        vendor: formData?.vendor,
        category: formData?.category,
        categoryIcon: 'Package',
        amount: parseFloat(formData?.amount),
        client: formData?.client || null,
        status: 'pending',
        description: formData?.description,
        receipt: formData?.receipt ? URL.createObjectURL(formData?.receipt) : null,
        receiptAlt: formData?.receipt ? `Receipt document for ${formData?.vendor} expense showing payment details and transaction information` : '',
        bank: formData?.bank || null,
        createdAt: new Date()?.toISOString()
      };

      setExpenses([newExpense, ...expenses]);
      setFilteredExpenses([newExpense, ...filteredExpenses]);
    }

    // Close the form after successful submission
    setShowQuickEntry(false);
    setEditingExpense(null);

    // Dispatch event to update bank balance
    if (formData?.bank) {
      const event = new CustomEvent('financialTransaction', {
        detail: {
          type: 'expense',
          amount: parseFloat(formData?.amount),
          bank: formData?.bank,
          date: formData?.date
        }
      });
      window.dispatchEvent(event);
    }
  };

  const handleEditExpense = (expenseId) => {
    const expense = expenses?.find(exp => exp?.id === expenseId);
    if (expense) {
      setEditingExpense(expense);
      setShowQuickEntry(true);
    }
  };

  const handleCancelEdit = () => {
    setEditingExpense(null);
    setShowQuickEntry(false);
  };

  const handleDeleteExpense = (expenseId) => {
    const confirmDelete = window.confirm('Tem certeza que deseja excluir esta despesa?');
    if (confirmDelete) {
      const expense = expenses?.find(exp => exp?.id === expenseId);
      
      setExpenses(expenses?.filter((exp) => exp?.id !== expenseId));
      setFilteredExpenses(filteredExpenses?.filter((exp) => exp?.id !== expenseId));

      // Refund bank balance when expense is deleted (if it was approved)
      if (expense?.status === 'approved' && expense?.bank) {
        const event = new CustomEvent('financialTransaction', {
          detail: {
            type: 'income', // Refund acts as income
            amount: expense?.amount,
            bank: expense?.bank,
            date: new Date().toISOString().split('T')[0]
          }
        });
        window.dispatchEvent(event);
      }
    }
  };

  const handleApproveExpense = (expenseId) => {
    const expense = expenses?.find(exp => exp?.id === expenseId);
    
    setExpenses(expenses?.map((exp) =>
      exp?.id === expenseId ? { ...exp, status: 'approved' } : exp
    ));
    setFilteredExpenses(filteredExpenses?.map((exp) =>
      exp?.id === expenseId ? { ...exp, status: 'approved' } : exp
    ));

    // Update bank balance when expense is approved
    if (expense?.bank) {
      const event = new CustomEvent('financialTransaction', {
        detail: {
          type: 'expense',
          amount: expense?.amount,
          bank: expense?.bank,
          date: expense?.date
        }
      });
      window.dispatchEvent(event);
    }
  };

  const handleRejectExpense = (expenseId, reason) => {
    setExpenses(expenses?.map((exp) =>
    exp?.id === expenseId ? { ...exp, status: 'rejected', rejectionReason: reason } : exp
    ));
    setFilteredExpenses(filteredExpenses?.map((exp) =>
    exp?.id === expenseId ? { ...exp, status: 'rejected', rejectionReason: reason } : exp
    ));
  };

  const handleSaveView = () => {
    console.log('Save current view');
  };

  const pendingExpenses = expenses?.filter((exp) => exp?.status === 'pending');

  const totalExpenses = filteredExpenses?.reduce((sum, exp) => sum + exp?.amount, 0);
  const approvedExpenses = filteredExpenses?.filter((exp) => exp?.status === 'approved')?.length;

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportAllExpenses = () => {
    exportHelpers?.exportExpensesToCSV(expenses, 'despesas_completo.csv');
    setShowExportModal(false);
  };

  const handleExportCurrentMonth = () => {
    const now = new Date();
    exportHelpers?.exportExpensesByMonthToCSV(
      expenses,
      now?.getMonth(),
      now?.getFullYear()
    );
    setShowExportModal(false);
  };

  const handleExportCustomMonth = (month, year) => {
    exportHelpers?.exportExpensesByMonthToCSV(expenses, month, year);
    setShowExportModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Helmet>
        <title>Gestão de Despesas - JR Wise Financial Control</title>
        <meta name="description" content="Sistema completo de rastreamento e categorização de despesas com fluxos de aprovação e monitoramento de orçamento" />
      </Helmet>
      <Header />
      <BreadcrumbNavigation />
      <div className="main-content bg-background">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Gestão de Despesas
              </h1>
              <p className="text-muted-foreground">
                Controle completo de despesas operacionais e por cliente
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="bg-card border border-border rounded-lg px-4 py-3">
                <div className="text-xs text-muted-foreground mb-1">Total Despesas</div>
                <div className="text-xl font-bold text-foreground">
                  R$ {totalExpenses?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg px-4 py-3">
                <div className="text-xs text-muted-foreground mb-1">Aprovadas</div>
                <div className="text-xl font-bold text-success">
                  {approvedExpenses}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg px-4 py-3">
                <div className="text-xs text-muted-foreground mb-1">Pendentes</div>
                <div className="text-xl font-bold text-warning">
                  {pendingExpenses?.length}
                </div>
              </div>

              <Button
                variant="default"
                iconName="Plus"
                onClick={() => setShowQuickEntry(true)}
              >
                Nova Despesa
              </Button>

              {currentUser?.permissions?.includes('approve') && pendingExpenses?.length > 0 && (
                <Button
                  variant="default"
                  iconName="CheckCircle"
                  onClick={() => setShowApprovalWorkflow(!showApprovalWorkflow)}
                >
                  {showApprovalWorkflow ? 'Ver Despesas' : 'Aprovar Despesas'}
                </Button>
              )}
            </div>
          </div>

          {showApprovalWorkflow ? (
            <ApprovalWorkflow
              pendingExpenses={pendingExpenses}
              onApprove={handleApproveExpense}
              onReject={handleRejectExpense}
            />
          ) : (
            <>
              <FilterToolbar
                onFilterChange={handleFilterChange}
                onSaveView={handleSaveView}
              />

              <div className="grid grid-cols-12 gap-6 mt-6" style={{ height: 'calc(100vh - 400px)' }}>
                <div className="col-span-2">
                  <ExpenseCategoryTree
                    onCategorySelect={handleCategorySelect}
                    selectedCategory={selectedCategory}
                  />
                </div>

                <div className="col-span-10">
                  <ExpenseGrid
                    expenses={filteredExpenses}
                    onEdit={handleEditExpense}
                    onDelete={handleDeleteExpense}
                    onApprove={handleApproveExpense}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <QuickActionToolbar />
      {/* Quick Entry Modal */}
      {showQuickEntry && (
        <QuickEntryForm 
          onSubmit={handleExpenseSubmit}
          editingExpense={editingExpense}
          onCancelEdit={handleCancelEdit}
          categories={EXPENSE_CATEGORIES}
          onClose={handleCancelEdit}
        />
      )}
      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExportAll={handleExportAllExpenses}
          onExportCurrentMonth={handleExportCurrentMonth}
          onExportCustomMonth={handleExportCustomMonth}
        />
      )}
      {/* Connection Status Indicator */}
      <ConnectionStatusIndicator
        connectionState={realtimeStatus?.connectionState}
        reconnectAttempts={realtimeStatus?.reconnectAttempts}
        bufferedUpdates={realtimeStatus?.bufferedUpdates}
        onManualReconnect={realtimeStatus?.manualReconnect}
      />
    </div>
  );
}